import React from 'react';
import { type Control, Controller, useFormContext } from 'react-hook-form';
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type { CMS1500FormData } from '../ClaimCMS1500Form';
import { formStyles } from '../ClaimCMS1500Form.styles';
import { DiagnosisPointerSelector } from '../DiagnosisPointerSelector';
import { ClaimTextField } from '../fields';
import { PlaceOfServiceSelect } from '../PlaceOfServiceSelect';

interface Box24Props {
  cleanCurrencyInput: (value: string) => string;
}

export const Box24_ServiceLines: React.FC<Box24Props> = ({ cleanCurrencyInput }) => {
  const { control } = useFormContext<CMS1500FormData>();
  return (
    <>
      <Typography variant="caption">24. SERVICE LINES</Typography>
      <Table sx={formStyles.serviceLineTable} size="small">
        <TableHead>
          <TableRow>
            <TableCell>A. Date(s) of Service</TableCell>
            <TableCell>B. POS</TableCell>
            <TableCell>C. EMG</TableCell>
            <TableCell>D. CPT/HCPCS</TableCell>
            <TableCell>E. Diag Ptr</TableCell>
            <TableCell>F. $ Charges</TableCell>
            <TableCell>G. Days/Units</TableCell>
            <TableCell>H. EPSDT</TableCell>
            <TableCell>I. ID Qual</TableCell>
            <TableCell>J. Rendering Provider ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[0, 1, 2, 3, 4, 5].map(index => (
            <TableRow key={index}>
              <TableCell>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Controller
                    name={`serviceLines.${index}.dateFrom`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <ClaimTextField
                        field={field}
                        fieldState={fieldState}
                        type="date"
                        size="small"
                        sx={{
                          width: '110px',
                          '& .MuiInputBase-input': {
                            padding: '2px 4px',
                            fontSize: '12px',
                          },
                          '& .MuiOutlinedInput-root': {
                            height: '24px',
                          },
                        }}
                      />
                    )}
                  />
                  <Controller
                    name={`serviceLines.${index}.dateTo`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <ClaimTextField
                        field={field}
                        fieldState={fieldState}
                        type="date"
                        size="small"
                        sx={{
                          width: '110px',
                          '& .MuiInputBase-input': {
                            padding: '2px 4px',
                            fontSize: '12px',
                          },
                          '& .MuiOutlinedInput-root': {
                            height: '24px',
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Controller
                  name={`serviceLines.${index}.placeOfService`}
                  control={control}
                  render={({ field: { disabled, ...restField } }) => (
                    <PlaceOfServiceSelect {...restField} size="small" disabled={disabled} />
                  )}
                />
              </TableCell>
              <TableCell align="center">
                <Controller
                  name={`serviceLines.${index}.emg`}
                  control={control}
                  render={({ field: { value, onChange, disabled, ...restField } }) => (
                    <Checkbox
                      {...restField}
                      checked={value === 'Y'}
                      onChange={e => onChange(e.target.checked ? 'Y' : '')}
                      disabled={disabled}
                      size="small"
                      sx={{ padding: 0 }}
                    />
                  )}
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                  <Controller
                    name={`serviceLines.${index}.cptHcpcs`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <ClaimTextField
                        field={field}
                        fieldState={fieldState}
                        size="small"
                        placeholder="CPT"
                        title={fieldState.error?.message}
                        sx={{
                          minWidth: '70px',
                          maxWidth: '100px',
                          '& .MuiInputBase-input': {
                            padding: '2px 4px',
                            fontSize: '12px',
                            fontFamily: 'monospace',
                            textTransform: 'uppercase',
                          },
                          '& .MuiOutlinedInput-root': {
                            height: '24px',
                          },
                        }}
                      />
                    )}
                  />
                  <Box sx={{ display: "flex", gap: 0.25 }}>
                    {[0, 1, 2, 3].map(modIndex => (
                      <Controller
                        key={modIndex}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
                        name={`serviceLines.${index}.modifier${modIndex + 1}` as any}
                        control={control}
                        render={({ field, fieldState }) => (
                          <ClaimTextField
                            field={field}
                            fieldState={fieldState}
                            size="small"
                            placeholder={modIndex === 0 ? 'M' : ''}
                            inputProps={{ maxLength: 2 }}
                            sx={{
                              minWidth: '32px',
                              maxWidth: '40px',
                              '& .MuiInputBase-input': {
                                padding: '2px 1px',
                                fontSize: '11px',
                                fontFamily: 'monospace',
                                textTransform: 'uppercase',
                                textAlign: 'center',
                              },
                              '& .MuiOutlinedInput-root': {
                                height: '24px',
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.15)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.3)',
                              },
                            }}
                          />
                        )}
                      />
                    ))}
                  </Box>
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '100px' }}>
                <Controller
                  name={`serviceLines.${index}.diagnosisPointer`}
                  control={control}
                  render={({ field }) => (
                    <DiagnosisPointerSelector
                      name={`serviceLines.${index}.diagnosisPointer`}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      control={control as Control<any>}
                      size="small"
                      disabled={field.disabled}
                    />
                  )}
                />
              </TableCell>
              <TableCell>
                <Controller
                  name={`serviceLines.${index}.charges`}
                  control={control}
                  render={({ field, fieldState }) => (
                    <ClaimTextField
                      field={{
                        ...field,
                        value: field.value || '',
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                          const cleaned = cleanCurrencyInput(e.target.value);
                          field.onChange(cleaned);
                        },
                      }}
                      fieldState={fieldState}
                      size="small"
                      placeholder="0.00"
                      title={fieldState.error?.message}
                      sx={{
                        minWidth: '90px',
                        maxWidth: '120px',
                        '& .MuiInputBase-input': {
                          padding: '2px 4px',
                          fontSize: '12px',
                          fontFamily: 'monospace',
                          textAlign: 'right',
                        },
                        '& .MuiOutlinedInput-root': {
                          height: '24px',
                        },
                      }}
                    />
                  )}
                />
              </TableCell>
              <TableCell>
                <Controller
                  name={`serviceLines.${index}.units`}
                  control={control}
                  render={({ field, fieldState }) => (
                    <ClaimTextField
                      field={field}
                      fieldState={fieldState}
                      size="small"
                      placeholder="1"
                      title={fieldState.error?.message}
                      sx={{
                        width: '50px',
                        '& .MuiInputBase-input': {
                          padding: '2px 4px',
                          fontSize: '12px',
                          textAlign: 'center',
                        },
                        '& .MuiOutlinedInput-root': {
                          height: '24px',
                        },
                      }}
                    />
                  )}
                />
              </TableCell>
              <TableCell>
                <Controller
                  name={`serviceLines.${index}.epsdt`}
                  control={control}
                  render={({ field, fieldState }) => (
                    <ClaimTextField
                      field={field}
                      fieldState={fieldState}
                      size="small"
                      sx={{
                        width: '40px',
                        '& .MuiInputBase-input': {
                          padding: '2px 4px',
                          fontSize: '12px',
                          textAlign: 'center',
                        },
                        '& .MuiOutlinedInput-root': {
                          height: '24px',
                        },
                      }}
                    />
                  )}
                />
              </TableCell>
              <TableCell>
                <Controller
                  name={`serviceLines.${index}.idQualifier`}
                  control={control}
                  render={({ field, fieldState }) => (
                    <ClaimTextField
                      field={field}
                      fieldState={fieldState}
                      size="small"
                      sx={{
                        width: '40px',
                        '& .MuiInputBase-input': {
                          padding: '2px 4px',
                          fontSize: '12px',
                          textAlign: 'center',
                        },
                        '& .MuiOutlinedInput-root': {
                          height: '24px',
                        },
                      }}
                    />
                  )}
                />
              </TableCell>
              <TableCell>
                <Controller
                  name={`serviceLines.${index}.renderingProviderId`}
                  control={control}
                  render={({ field, fieldState }) => (
                    <ClaimTextField
                      field={field}
                      fieldState={fieldState}
                      size="small"
                      placeholder="NPI"
                      sx={{
                        minWidth: '110px',
                        maxWidth: '140px',
                        '& .MuiInputBase-input': {
                          padding: '2px 4px',
                          fontSize: '12px',
                          fontFamily: 'monospace',
                        },
                        '& .MuiOutlinedInput-root': {
                          height: '24px',
                        },
                      }}
                    />
                  )}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};
